import * as chat from '@app/packages/chat/state'
import type { ChatServerAction } from '@app/packages/chat/state'
import { chatServerReducer, initialChatServerState } from '@app/packages/chat/state'

import { callApi } from '@app/store/actions/api'
import { isAuthenticatedSelector } from '@app/store/slices/session'
import { createThunk } from '@app/store/thunk'

import { chatStateSelector, chatStateSlice } from './slice'

/** Apply a chat package action to the mounted slice via the package reducer. */
export const applyChatAction = (action: ChatServerAction) =>
  createThunk(dispatch => {
    dispatch(chatStateSlice.update(state => chatServerReducer(state ?? initialChatServerState, action)))
  })

export const upsertChannels = bindChatAction(chat.upsertChannels)
export const upsertChannel = bindChatAction(chat.upsertChannel)
export const removeChannel = bindChatAction(chat.removeChannel)
export const upsertMessages = bindChatAction(chat.upsertMessages)
export const addMessage = bindChatAction(chat.addMessage)
export const editMessage = bindChatAction(chat.editMessage)
export const cancelMessage = bindChatAction(chat.cancelMessage)
export const outdateMessage = bindChatAction(chat.outdateMessage)
export const removeMessage = bindChatAction(chat.removeMessage)
export const setUnread = bindChatAction(chat.setUnread)
export const incrementUnread = bindChatAction(chat.incrementUnread)
export const setUnreadBulk = bindChatAction(chat.setUnreadBulk)
export const markReadOptimistic = bindChatAction(chat.markReadOptimistic)
export const applyReadReceipt = bindChatAction(chat.applyReadReceipt)
export const setTyping = bindChatAction(chat.setTyping)
export const upsertResolvedParticipants = bindChatAction(chat.upsertResolvedParticipants)
export const setOwner = bindChatAction(chat.setOwner)
export const setConnected = bindChatAction(chat.setConnected)
export const setReady = bindChatAction(chat.setReady)
export const setChannelsStatus = bindChatAction(chat.setChannelsStatus)

/**
 * Resolve participant external_ids to display name/avatar via the Rails resolve
 * endpoint, caching per external_id. Only uncached ids are fetched (one batched
 * request). Best-effort — callers fall back to the embedded participant name.
 */
export const resolveParticipants = (externalIds: string[]) =>
  createThunk(async (dispatch, getState) => {
    // The endpoint authenticates a user; a guest would 401 on every message load, and the
    // only peer it could ask about — support — is known from the channel's type anyway.
    if (!isAuthenticatedSelector(getState())) return
    const { postParticipantsResolve } = await import('@app/packages/api/v3/actions/participants')
    const cached = chatStateSelector(getState()).resolved
    const missing = [...new Set(externalIds)].filter(id => !!id && !cached[id])
    if (!missing.length) return
    const { participants } = await dispatch(callApi(postParticipantsResolve(missing)))
    dispatch(upsertResolvedParticipants(participants))
  })

function bindChatAction<Args extends unknown[]>(creator: (...args: Args) => ChatServerAction) {
  return (...args: Args) => applyChatAction(creator(...args))
}
